<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useQuery, useQueryClient } from '@tanstack/vue-query';
import axiosInstance from '@/services/interceptor.services';
import { TranslationService } from '@/services/translation.services';

const router = useRouter();
const { t } = useI18n({ useScope: 'global' });
const queryClient = useQueryClient();

/* ── Sélecteur organisation ───────────────────────────────────────────────── */
const orgOpen = ref(false);
const orgSearch = ref('');
const orgPage = ref(1);
const ORG_PAGE_SIZE = 20;

const { data: orgResp, isFetching: orgLoading } = useQuery({
  queryKey: ['orgs-picker-all'],
  queryFn: async () => {
    const first = await axiosInstance.get(
      `organizations/search?pagination=true&page=1&pageSize=50`,
    );
    const meta = first.data;
    const total: number = meta?.total ?? 0;
    const pageSize: number = meta?.pageSize ?? 50;
    const extract = (r: any) => {
      const d = r?.data;
      return Array.isArray(d) ? d : Array.isArray(d?.data) ? d.data : [];
    };
    let all = extract(first);
    if (total > pageSize) {
      const pages = Array.from(
        { length: Math.ceil(total / pageSize) - 1 },
        (_, i) => i + 2,
      );
      const rest = await Promise.all(
        pages.map((p) =>
          axiosInstance
            .get(`organizations/search?pagination=true&page=${p}&pageSize=50`)
            .then(extract),
        ),
      );
      all = [...all, ...rest.flat()];
    }
    return all;
  },
  staleTime: 60_000,
});

const allOrgs = computed<any[]>(() => {
  const d = orgResp.value;
  return Array.isArray(d) ? d : [];
});

const filteredOrgs = computed(() => {
  const q = orgSearch.value.trim().toLowerCase();
  return q
    ? allOrgs.value.filter((o) => o.name?.toLowerCase().includes(q))
    : allOrgs.value;
});

const orgPages = computed(() =>
  Math.max(1, Math.ceil(filteredOrgs.value.length / ORG_PAGE_SIZE)),
);
const orgList = computed(() => {
  const start = (orgPage.value - 1) * ORG_PAGE_SIZE;
  return filteredOrgs.value.slice(start, start + ORG_PAGE_SIZE);
});

const selectedOrg = ref<{ id: string; name: string } | null>(null);

function selectOrg(org: any) {
  selectedOrg.value = { id: org.id, name: org.name };
  form1.value.organizationId = org.id;
  orgOpen.value = false;
  orgSearch.value = '';
  orgPage.value = 1;
}
function clearOrg() {
  selectedOrg.value = null;
  form1.value.organizationId = '';
}

function onOrgSearch(e: Event) {
  orgSearch.value = (e.target as HTMLInputElement).value;
  orgPage.value = 1;
}

/* ── Étapes ──────────────────────────────────────────────────────────────── */
const step = ref<1 | 2 | 3>(1);
const createdId = ref<string | null>(null);

const STEPS = computed(() => [
  { n: 1, label: t('createCommerce.steps.informations'), icon: 'pi-shop' },
  { n: 2, label: t('createCommerce.steps.horaires'), icon: 'pi-clock' },
  { n: 3, label: t('createCommerce.steps.contacts'), icon: 'pi-users' },
]);

/* ── Constantes ───────────────────────────────────────────────────────────── */
const CURRENCIES = computed(() => [
  { value: 'EUR', label: t('createCommerce.currencies.EUR') },
  { value: 'USD', label: t('createCommerce.currencies.USD') },
  { value: 'GBP', label: t('createCommerce.currencies.GBP') },
  { value: 'CHF', label: t('createCommerce.currencies.CHF') },
  { value: 'CAD', label: t('createCommerce.currencies.CAD') },
  { value: 'MAD', label: t('createCommerce.currencies.MAD') },
  { value: 'XOF', label: t('createCommerce.currencies.XOF') },
]);
const TIMEZONES = computed(() => [
  {
    value: 'Europe/Brussels',
    label: t('createCommerce.timezones.EuropeBrussels'),
  },
  { value: 'Europe/London', label: t('createCommerce.timezones.EuropeLondon') },
  {
    value: 'Europe/Amsterdam',
    label: t('createCommerce.timezones.EuropeAmsterdam'),
  },
  { value: 'Europe/Zurich', label: t('createCommerce.timezones.EuropeZurich') },
  {
    value: 'Africa/Casablanca',
    label: t('createCommerce.timezones.AfricaCasablanca'),
  },
  {
    value: 'Africa/Abidjan',
    label: t('createCommerce.timezones.AfricaAbidjan'),
  },
  {
    value: 'America/New_York',
    label: t('createCommerce.timezones.AmericaNewYork'),
  },
  {
    value: 'America/Toronto',
    label: t('createCommerce.timezones.AmericaToronto'),
  },
  { value: 'UTC', label: t('createCommerce.timezones.UTC') },
]);
const COUNTRIES = computed(() => [
  { value: 'BE', label: t('createCommerce.countries.BE') },
  { value: 'FR', label: t('createCommerce.countries.FR') },
  { value: 'NL', label: t('createCommerce.countries.NL') },
  { value: 'LU', label: t('createCommerce.countries.LU') },
  { value: 'DE', label: t('createCommerce.countries.DE') },
  { value: 'CH', label: t('createCommerce.countries.CH') },
  { value: 'GB', label: t('createCommerce.countries.GB') },
  { value: 'US', label: t('createCommerce.countries.US') },
  { value: 'CA', label: t('createCommerce.countries.CA') },
  { value: 'MA', label: t('createCommerce.countries.MA') },
  { value: 'CI', label: t('createCommerce.countries.CI') },
  { value: 'SN', label: t('createCommerce.countries.SN') },
]);
const WEEKDAYS = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
] as const;
const WEEKDAY_LABELS = computed<Record<string, string>>(() => ({
  monday: t('days.monday'),
  tuesday: t('days.tuesday'),
  wednesday: t('days.wednesday'),
  thursday: t('days.thursday'),
  friday: t('days.friday'),
  saturday: t('days.saturday'),
  sunday: t('days.sunday'),
}));
const POSITIONS = computed(() => [
  { value: 'manager', label: t('createCommerce.positions.manager') },
  { value: 'ceo', label: t('createCommerce.positions.ceo') },
  { value: 'cfo', label: t('createCommerce.positions.cfo') },
  { value: 'cto', label: t('createCommerce.positions.cto') },
  { value: 'accountant', label: t('createCommerce.positions.accountant') },
  {
    value: 'legal_representative',
    label: t('createCommerce.positions.legal_representative'),
  },
  {
    value: 'technical_contact',
    label: t('createCommerce.positions.technical_contact'),
  },
  {
    value: 'billing_contact',
    label: t('createCommerce.positions.billing_contact'),
  },
  {
    value: 'support_contact',
    label: t('createCommerce.positions.support_contact'),
  },
]);

/* ── Étape 1 : Informations ───────────────────────────────────────────────── */
const form1 = ref({
  designation: '',
  orderCode: '',
  currency: 'EUR',
  timezone: 'Europe/Brussels',
  country: 'BE',
  address: '',
  organizationId: '',
  descLang: 'fr' as 'fr' | 'en' | 'nl',
  descFr: '',
  descEn: '',
  descNl: '',
  tagLang: 'fr' as 'fr' | 'en' | 'nl',
  tagFr: '',
  tagEn: '',
  tagNl: '',
  bdLang: 'fr' as 'fr' | 'en' | 'nl',
  bdFr: '',
  bdEn: '',
  bdNl: '',
  city: '',
  foundedYear: '',
});

const orderCodeManuallyEdited = ref(false);

function generateOrderCode(): string {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  let code = '';
  for (let i = 0; i < 3; i++)
    code += chars[Math.floor(Math.random() * chars.length)];
  return code;
}

watch(
  () => form1.value.designation,
  () => {
    if (!orderCodeManuallyEdited.value) {
      form1.value.orderCode = generateOrderCode();
    }
  },
  { immediate: false },
);

const canStep1 = computed(
  () =>
    form1.value.designation.trim() &&
    form1.value.orderCode.trim() &&
    form1.value.currency &&
    form1.value.organizationId.trim(),
);

// N'affiche les erreurs de champ (bordure rouge + message) qu'après une
// première tentative de soumission, pas dès l'ouverture du formulaire.
const attemptedSubmit1 = ref(false);
const showNameError = computed(
  () => attemptedSubmit1.value && !form1.value.designation.trim(),
);
const showOrderCodeError = computed(
  () => attemptedSubmit1.value && !form1.value.orderCode.trim(),
);
const showOrgError = computed(
  () => attemptedSubmit1.value && !form1.value.organizationId.trim(),
);

const isSaving1 = ref(false);
const error1 = ref<string | null>(null);
const translatingDesc = ref(false);
const translatingTag = ref(false);
const translatingBd = ref(false);

async function translateFields(
  src: 'fr' | 'en' | 'nl',
  getText: () => string,
  setText: (lang: 'fr' | 'en' | 'nl', val: string) => void,
  translatingRef: { value: boolean },
) {
  const text = getText();
  if (!text.trim() || translatingRef.value) return;
  translatingRef.value = true;
  try {
    const translated = await TranslationService.translateText(text, src);
    (['fr', 'en', 'nl'] as const)
      .filter((l) => l !== src)
      .forEach((lang) => setText(lang, translated[lang]));
  } catch {
    /* silencieux */
  } finally {
    translatingRef.value = false;
  }
}

function translateDesc() {
  const src = form1.value.descLang;
  return translateFields(
    src,
    () =>
      src === 'fr'
        ? form1.value.descFr
        : src === 'en'
          ? form1.value.descEn
          : form1.value.descNl,
    (lang, val) => {
      if (lang === 'fr') form1.value.descFr = val;
      if (lang === 'en') form1.value.descEn = val;
      if (lang === 'nl') form1.value.descNl = val;
    },
    translatingDesc,
  );
}

function translateTag() {
  const src = form1.value.tagLang;
  return translateFields(
    src,
    () =>
      src === 'fr'
        ? form1.value.tagFr
        : src === 'en'
          ? form1.value.tagEn
          : form1.value.tagNl,
    (lang, val) => {
      if (lang === 'fr') form1.value.tagFr = val;
      if (lang === 'en') form1.value.tagEn = val;
      if (lang === 'nl') form1.value.tagNl = val;
    },
    translatingTag,
  );
}

function translateBd() {
  const src = form1.value.bdLang;
  return translateFields(
    src,
    () =>
      src === 'fr'
        ? form1.value.bdFr
        : src === 'en'
          ? form1.value.bdEn
          : form1.value.bdNl,
    (lang, val) => {
      if (lang === 'fr') form1.value.bdFr = val;
      if (lang === 'en') form1.value.bdEn = val;
      if (lang === 'nl') form1.value.bdNl = val;
    },
    translatingBd,
  );
}

async function submitStep1() {
  attemptedSubmit1.value = true;
  if (!canStep1.value || isSaving1.value) return;
  error1.value = null;
  isSaving1.value = true;
  try {
    const body: any = {
      designation: form1.value.designation.trim(),
      orderCode: form1.value.orderCode.trim(),
      currency: form1.value.currency,
      timezone: form1.value.timezone,
      organizationId: form1.value.organizationId.trim(),
    };
    if (form1.value.address.trim()) body.address = form1.value.address.trim();
    if (form1.value.country) body.country = form1.value.country;

    // bannerDescription — toujours envoyer les 3 langues (requises par l'API)
    body.bannerDescription = {
      fr: form1.value.bdFr || '',
      en: form1.value.bdEn || '',
      nl: form1.value.bdNl || '',
    };

    // metadata
    const meta: any = {};
    if (form1.value.city.trim()) meta.city = form1.value.city.trim();
    if (form1.value.foundedYear)
      meta.foundedYear = Number(form1.value.foundedYear);
    if (
      form1.value.tagFr.trim() ||
      form1.value.tagEn.trim() ||
      form1.value.tagNl.trim()
    ) {
      meta.taglineCodes = {
        fr: form1.value.tagFr
          ? form1.value.tagFr
              .split(',')
              .map((s) => s.trim())
              .filter(Boolean)
          : [],
        en: form1.value.tagEn
          ? form1.value.tagEn
              .split(',')
              .map((s) => s.trim())
              .filter(Boolean)
          : [],
        nl: form1.value.tagNl
          ? form1.value.tagNl
              .split(',')
              .map((s) => s.trim())
              .filter(Boolean)
          : [],
      };
    }
    if (Object.keys(meta).length) body.metadata = meta;
    const res = await axiosInstance.post('restaurants/create', body);
    const d = res.data?.data ?? res.data;
    createdId.value = d?.id ?? null;
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['commerces'] }),
      queryClient.invalidateQueries({ queryKey: ['GetRestaurants'] }),
    ]);
    step.value = 2;
  } catch (err: any) {
    const raw = err?.response?.data?.message;
    error1.value = Array.isArray(raw)
      ? raw[0]
      : (raw ?? err?.message ?? t('createCommerce.error1Fallback'));
  } finally {
    isSaving1.value = false;
  }
}

/* ── Étape 2 : Horaires ───────────────────────────────────────────────────── */
type DaySchedule = { enabled: boolean; open: string; close: string };
type WeekSchedule = Record<string, DaySchedule>;

const weekSchedule = ref<WeekSchedule>({
  monday: { enabled: true, open: '09:00', close: '22:00' },
  tuesday: { enabled: true, open: '09:00', close: '22:00' },
  wednesday: { enabled: true, open: '09:00', close: '22:00' },
  thursday: { enabled: true, open: '09:00', close: '22:00' },
  friday: { enabled: true, open: '09:00', close: '22:00' },
  saturday: { enabled: false, open: '10:00', close: '23:00' },
  sunday: { enabled: false, open: '10:00', close: '23:00' },
});

/* Time picker custom */
const timePickerOpen = ref<string | null>(null); // "monday-open" | "monday-close" | null

function parseTime(t: string) {
  const [h, m] = t.split(':').map(Number);
  return { h: h ?? 0, m: m ?? 0 };
}
function fmtTime(h: number, m: number) {
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}
function adjustHour(day: string, field: 'open' | 'close', delta: number) {
  const t = parseTime(weekSchedule.value[day][field]);
  weekSchedule.value[day][field] = fmtTime((t.h + delta + 24) % 24, t.m);
}
function adjustMin(day: string, field: 'open' | 'close', delta: number) {
  const t = parseTime(weekSchedule.value[day][field]);
  const newM = (t.m + delta + 60) % 60;
  weekSchedule.value[day][field] = fmtTime(t.h, newM);
}
function toggleTimePicker(key: string) {
  timePickerOpen.value = timePickerOpen.value === key ? null : key;
}
function closeTimePicker() {
  timePickerOpen.value = null;
}

const isSaving2 = ref(false);
const error2 = ref<string | null>(null);

async function submitStep2() {
  if (!createdId.value) return;
  const enabledDays = WEEKDAYS.filter((d) => weekSchedule.value[d].enabled);
  if (!enabledDays.length) {
    step.value = 3;
    return;
  }
  isSaving2.value = true;
  error2.value = null;
  try {
    const body: Record<string, { open: string; close: string }[]> = {};
    for (const d of enabledDays) {
      body[d] = [
        {
          open: weekSchedule.value[d].open,
          close: weekSchedule.value[d].close,
        },
      ];
    }
    await axiosInstance.put(
      `restaurants/${createdId.value}/schedules/weekday-schedule`,
      body,
    );
    step.value = 3;
  } catch (err: any) {
    const raw = err?.response?.data?.message;
    error2.value = Array.isArray(raw)
      ? raw[0]
      : (raw ?? t('createCommerce.error2Fallback'));
  } finally {
    isSaving2.value = false;
  }
}

/* ── Étape 3 : Contacts ───────────────────────────────────────────────────── */
type ContactRow = {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  position: string;
  isPrimary: boolean;
};
const contacts = ref<ContactRow[]>([
  {
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    position: 'manager',
    isPrimary: true,
  },
]);

function addContact() {
  contacts.value.push({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    position: 'manager',
    isPrimary: false,
  });
}
function removeContact(i: number) {
  contacts.value.splice(i, 1);
}
function setPrimary(i: number) {
  contacts.value.forEach((c, idx) => (c.isPrimary = idx === i));
}

const isSaving3 = ref(false);
const error3 = ref<string | null>(null);
const done = ref(false);

async function submitStep3() {
  if (!createdId.value) return;
  const valid = contacts.value.filter(
    (c) => c.firstName.trim() || c.lastName.trim() || c.email.trim(),
  );
  if (!valid.length) {
    done.value = true;
    return;
  }
  isSaving3.value = true;
  error3.value = null;
  try {
    await Promise.all(
      valid.map((c) =>
        axiosInstance.post(`restaurants/${createdId.value}/contacts/add`, {
          firstName: c.firstName.trim() || undefined,
          lastName: c.lastName.trim() || undefined,
          email: c.email.trim() || undefined,
          phone: c.phone.trim() || undefined,
          position: c.position || undefined,
          isPrimary: c.isPrimary,
          languages: ['fr'],
        }),
      ),
    );
    done.value = true;
  } catch (err: any) {
    const raw = err?.response?.data?.message;
    error3.value = Array.isArray(raw)
      ? raw[0]
      : (raw ?? t('createCommerce.error3Fallback'));
  } finally {
    isSaving3.value = false;
  }
}

function goToCommerce() {
  if (createdId.value) router.push(`/commerces/${createdId.value}`);
  else router.push('/commerces');
}
</script>

<template>
  <div class="ccp">
    <!-- Breadcrumb -->
    <nav class="ccp__bc">
      <button class="ccp__bc-link" @click="router.push('/commerces')">
        {{ t('createCommerce.breadcrumb') }}
      </button>
      <i class="pi pi-angle-right ccp__bc-sep" />
      <span class="ccp__bc-cur">{{ t('createCommerce.bcNew') }}</span>
    </nav>

    <!-- ── Succès final ────────────────────────────────────────────────────── -->
    <div v-if="done" class="ccp__success">
      <div class="ccp__success-icon"><i class="pi pi-check" /></div>
      <h2 class="ccp__success-title">{{ t('createCommerce.successTitle') }}</h2>
      <p class="ccp__success-sub">{{ t('createCommerce.successBody') }}</p>
      <div class="ccp__success-actions">
        <button
          class="ccp__btn ccp__btn--ghost"
          @click="router.push('/commerces')"
        >
          <i class="pi pi-arrow-left" /> {{ t('createCommerce.backList') }}
        </button>
        <button class="ccp__btn ccp__btn--primary" @click="goToCommerce">
          <i class="pi pi-eye" /> {{ t('createCommerce.viewStore') }}
        </button>
      </div>
    </div>

    <template v-else>
      <!-- Header -->
      <div class="ccp__header">
        <button class="ccp__back" @click="router.push('/commerces')">
          <i class="pi pi-arrow-left" />
        </button>
        <div class="ccp__header-text">
          <h1 class="ccp__title">{{ t('createCommerce.title') }}</h1>
          <p class="ccp__sub">
            {{
              t('createCommerce.stepSub', {
                label: STEPS[step - 1].label,
                step,
                total: STEPS.length,
              })
            }}
          </p>
        </div>
      </div>

      <!-- Stepper -->
      <div class="ccp__stepper">
        <div
          v-for="s in STEPS"
          :key="s.n"
          class="ccp__step"
          :class="{
            'ccp__step--done': step > s.n,
            'ccp__step--active': step === s.n,
          }"
        >
          <div class="ccp__step-dot">
            <i v-if="step > s.n" class="pi pi-check" />
            <i v-else class="pi" :class="s.icon" />
          </div>
          <span class="ccp__step-label">{{ s.label }}</span>
          <div v-if="s.n < STEPS.length" class="ccp__step-line" />
        </div>
      </div>

      <!-- ══ Étape 1 : Informations ════════════════════════════════════════════ -->
      <div v-if="step === 1" class="ccp__body">
        <section class="ccp__section">
          <div class="ccp__section-hd">
            <div class="ccp__section-ico"><i class="pi pi-shop" /></div>
            <div>
              <p class="ccp__section-title">
                {{ t('createCommerce.step1Title') }}
              </p>
              <p class="ccp__section-sub">{{ t('createCommerce.step1Sub') }}</p>
            </div>
          </div>
          <div class="ccp__fields">
            <div class="ccp__field-row">
              <div class="ccp__field ccp__field--span2">
                <label class="ccp__label"
                  >{{ t('createCommerce.name') }}
                  <span class="ccp__req">*</span></label
                >
                <input
                  v-model="form1.designation"
                  class="ccp__input"
                  :class="{ 'ccp__input--error': showNameError }"
                  type="text"
                  :placeholder="t('createCommerce.namePlaceholder')"
                />
                <p v-if="showNameError" class="ccp__field-error">
                  {{ t('createCommerce.requiredField') }}
                </p>
              </div>
            </div>
            <div class="ccp__field-row">
              <div class="ccp__field">
                <label class="ccp__label"
                  >{{ t('createCommerce.orderCode') }}
                  <span class="ccp__req">*</span></label
                >
                <div class="ccp__input-wrap">
                  <input
                    v-model="form1.orderCode"
                    class="ccp__input"
                    :class="{ 'ccp__input--error': showOrderCodeError }"
                    type="text"
                    :placeholder="t('createCommerce.codePlaceholder')"
                    maxlength="3"
                    style="
                      text-transform: uppercase;
                      letter-spacing: 0.15em;
                      font-weight: 800;
                    "
                    @input="
                      orderCodeManuallyEdited = true;
                      form1.orderCode = form1.orderCode
                        .toUpperCase()
                        .slice(0, 3);
                    "
                  />
                  <button
                    class="ccp__input-reset"
                    :title="t('createCommerce.codeHint')"
                    @click="form1.orderCode = generateOrderCode()"
                  >
                    <i class="pi pi-refresh" />
                  </button>
                </div>
                <span v-if="!showOrderCodeError" class="ccp__hint">{{
                  t('createCommerce.codeHint')
                }}</span>
                <p v-else class="ccp__field-error">
                  {{ t('createCommerce.requiredField') }}
                </p>
              </div>
              <div class="ccp__field">
                <label class="ccp__label">{{
                  t('createCommerce.address')
                }}</label>
                <input
                  v-model="form1.address"
                  class="ccp__input"
                  type="text"
                  :placeholder="t('createCommerce.addressPlaceholder')"
                />
              </div>
            </div>
          </div>
        </section>

        <div class="ccp__divider" />

        <section class="ccp__section">
          <div class="ccp__section-hd">
            <div class="ccp__section-ico"><i class="pi pi-cog" /></div>
            <div>
              <p class="ccp__section-title">
                {{ t('createCommerce.settingsTitle') }}
              </p>
              <p class="ccp__section-sub">
                {{ t('createCommerce.settingsSub') }}
              </p>
            </div>
          </div>
          <div class="ccp__fields">
            <div class="ccp__field-row">
              <div class="ccp__field">
                <label class="ccp__label"
                  >{{ t('createCommerce.currency') }}
                  <span class="ccp__req">*</span></label
                >
                <div class="ccp__select-wrap">
                  <select v-model="form1.currency" class="ccp__select">
                    <option
                      v-for="c in CURRENCIES"
                      :key="c.value"
                      :value="c.value"
                    >
                      {{ c.label }}
                    </option>
                  </select>
                  <i class="pi pi-chevron-down ccp__select-icon" />
                </div>
              </div>
              <div class="ccp__field">
                <label class="ccp__label">{{
                  t('createCommerce.timezone')
                }}</label>
                <div class="ccp__select-wrap">
                  <select v-model="form1.timezone" class="ccp__select">
                    <option
                      v-for="tz in TIMEZONES"
                      :key="tz.value"
                      :value="tz.value"
                    >
                      {{ tz.label }}
                    </option>
                  </select>
                  <i class="pi pi-chevron-down ccp__select-icon" />
                </div>
              </div>
            </div>
            <div class="ccp__field-row">
              <div class="ccp__field">
                <label class="ccp__label">{{
                  t('createCommerce.country')
                }}</label>
                <div class="ccp__select-wrap">
                  <select v-model="form1.country" class="ccp__select">
                    <option
                      v-for="c in COUNTRIES"
                      :key="c.value"
                      :value="c.value"
                    >
                      {{ c.label }}
                    </option>
                  </select>
                  <i class="pi pi-chevron-down ccp__select-icon" />
                </div>
              </div>
              <div class="ccp__field">
                <label class="ccp__label"
                  >{{ t('createCommerce.org') }}
                  <span class="ccp__req">*</span></label
                >
                <!-- Valeur sélectionnée -->
                <div v-if="selectedOrg" class="ccp__org-selected">
                  <span class="ccp__org-selected-name">{{
                    selectedOrg.name
                  }}</span>
                  <button class="ccp__org-clear" @click="clearOrg">
                    <i class="pi pi-times" />
                  </button>
                </div>
                <!-- Trigger -->
                <button
                  v-else
                  class="ccp__org-trigger"
                  :class="{ 'ccp__org-trigger--error': showOrgError }"
                  @click="orgOpen = true"
                >
                  <i class="pi pi-building" />
                  <span>{{ t('createCommerce.orgPlaceholder') }}</span>
                  <i
                    class="pi pi-chevron-down"
                    style="margin-left: auto; font-size: 0.75rem"
                  />
                </button>
                <p v-if="showOrgError" class="ccp__field-error">
                  {{ t('createCommerce.requiredField') }}
                </p>

                <!-- Dropdown -->
                <div v-if="orgOpen" class="ccp__org-drop">
                  <div class="ccp__org-search-wrap">
                    <i class="pi pi-search ccp__org-search-ico" />
                    <input
                      class="ccp__org-search"
                      type="text"
                      :placeholder="t('createCommerce.orgSearch')"
                      autofocus
                      @input="onOrgSearch"
                    />
                    <button
                      class="ccp__org-close"
                      @click="
                        orgOpen = false;
                        orgSearch = '';
                        orgPage = 1;
                      "
                    >
                      <i class="pi pi-times" />
                    </button>
                  </div>

                  <div class="ccp__org-list">
                    <div v-if="orgLoading" class="ccp__org-loading">
                      <span class="ccp__spinner" />
                      {{ t('createCommerce.orgLoading') }}
                    </div>
                    <template v-else-if="orgList.length">
                      <button
                        v-for="org in orgList"
                        :key="org.id"
                        class="ccp__org-item"
                        @click="selectOrg(org)"
                      >
                        <div class="ccp__org-item-av">
                          {{ org.name?.[0]?.toUpperCase() ?? '?' }}
                        </div>
                        <div class="ccp__org-item-body">
                          <span class="ccp__org-item-name">{{ org.name }}</span>
                          <span class="ccp__org-item-sub">{{
                            org.type ?? ''
                          }}</span>
                        </div>
                      </button>
                    </template>
                    <div v-else class="ccp__org-empty">
                      {{ t('createCommerce.orgEmpty') }}
                    </div>
                  </div>

                  <!-- Pagination -->
                  <div v-if="orgPages > 1" class="ccp__org-pag">
                    <button
                      class="ccp__org-pag-btn"
                      :disabled="orgPage <= 1"
                      @click="orgPage--"
                    >
                      <i class="pi pi-chevron-left" />
                    </button>
                    <span class="ccp__org-pag-info"
                      >{{ orgPage }} / {{ orgPages }}</span
                    >
                    <button
                      class="ccp__org-pag-btn"
                      :disabled="orgPage >= orgPages"
                      @click="orgPage++"
                    >
                      <i class="pi pi-chevron-right" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <div class="ccp__divider" />

        <div class="ccp__divider" />

        <!-- ─ Métadonnées ─ -->
        <section class="ccp__section">
          <div class="ccp__section-hd">
            <div class="ccp__section-ico"><i class="pi pi-map-marker" /></div>
            <div>
              <p class="ccp__section-title">
                {{ t('createCommerce.extraTitle') }}
              </p>
              <p class="ccp__section-sub">{{ t('createCommerce.extraSub') }}</p>
            </div>
          </div>
          <div class="ccp__fields">
            <div class="ccp__field-row">
              <div class="ccp__field">
                <label class="ccp__label">{{ t('createCommerce.city') }}</label>
                <input
                  v-model="form1.city"
                  class="ccp__input"
                  type="text"
                  :placeholder="t('createCommerce.city')"
                />
              </div>
              <div class="ccp__field">
                <label class="ccp__label">{{ t('createCommerce.year') }}</label>
                <input
                  v-model="form1.foundedYear"
                  class="ccp__input"
                  type="number"
                  :placeholder="t('createCommerce.year')"
                  min="1900"
                  :max="new Date().getFullYear()"
                />
              </div>
            </div>
            <div class="ccp__field">
              <div class="ccp__translabel-row">
                <label class="ccp__label">{{
                  t('createCommerce.taglines')
                }}</label>
                <div class="ccp__select-wrap" style="max-width: 10rem">
                  <select
                    v-model="form1.tagLang"
                    class="ccp__select ccp__select--sm"
                  >
                    <option value="fr">{{ t('createCommerce.langFr') }}</option>
                    <option value="en">{{ t('createCommerce.langEn') }}</option>
                    <option value="nl">{{ t('createCommerce.langNl') }}</option>
                  </select>
                  <i class="pi pi-chevron-down ccp__select-icon" />
                </div>
              </div>
              <div class="ccp__field-row ccp__field-row--3">
                <div class="ccp__field">
                  <label class="ccp__label">
                    <span
                      class="ccp__lang-badge"
                      :class="
                        form1.tagLang === 'fr' ? 'ccp__lang-badge--active' : ''
                      "
                      >FR</span
                    >
                    <span
                      v-if="translatingTag && form1.tagLang !== 'fr'"
                      class="ccp__translating-dot"
                    />
                  </label>
                  <input
                    v-model="form1.tagFr"
                    class="ccp__input"
                    :class="form1.tagLang === 'fr' ? 'ccp__input--primary' : ''"
                    type="text"
                    :placeholder="t('createCommerce.taglineFrPlaceholder')"
                    @blur="form1.tagLang === 'fr' && translateTag()"
                  />
                </div>
                <div class="ccp__field">
                  <label class="ccp__label">
                    <span
                      class="ccp__lang-badge"
                      :class="
                        form1.tagLang === 'en' ? 'ccp__lang-badge--active' : ''
                      "
                      >EN</span
                    >
                    <span
                      v-if="translatingTag && form1.tagLang !== 'en'"
                      class="ccp__translating-dot"
                    />
                  </label>
                  <input
                    v-model="form1.tagEn"
                    class="ccp__input"
                    :class="form1.tagLang === 'en' ? 'ccp__input--primary' : ''"
                    type="text"
                    :placeholder="t('createCommerce.taglineEnPlaceholder')"
                    @blur="form1.tagLang === 'en' && translateTag()"
                  />
                </div>
                <div class="ccp__field">
                  <label class="ccp__label">
                    <span
                      class="ccp__lang-badge"
                      :class="
                        form1.tagLang === 'nl' ? 'ccp__lang-badge--active' : ''
                      "
                      >NL</span
                    >
                    <span
                      v-if="translatingTag && form1.tagLang !== 'nl'"
                      class="ccp__translating-dot"
                    />
                  </label>
                  <input
                    v-model="form1.tagNl"
                    class="ccp__input"
                    :class="form1.tagLang === 'nl' ? 'ccp__input--primary' : ''"
                    type="text"
                    :placeholder="t('createCommerce.taglineNlPlaceholder')"
                    @blur="form1.tagLang === 'nl' && translateTag()"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        <div class="ccp__divider" />

        <!-- Banner Description -->
        <section class="ccp__section">
          <div class="ccp__section-hd">
            <div class="ccp__section-ico"><i class="pi pi-image" /></div>
            <div>
              <p class="ccp__section-title">
                {{ t('createCommerce.bannerTitle') }}
              </p>
              <p class="ccp__section-sub">
                {{ t('createCommerce.bannerSub') }}
              </p>
            </div>
          </div>
          <div class="ccp__fields">
            <div class="ccp__translabel-row">
              <label class="ccp__label">{{
                t('createCommerce.bannerLangLabel')
              }}</label>
              <div class="ccp__select-wrap" style="max-width: 10rem">
                <select
                  v-model="form1.bdLang"
                  class="ccp__select ccp__select--sm"
                >
                  <option value="fr">{{ t('createCommerce.langFr') }}</option>
                  <option value="en">{{ t('createCommerce.langEn') }}</option>
                  <option value="nl">{{ t('createCommerce.langNl') }}</option>
                </select>
                <i class="pi pi-chevron-down ccp__select-icon" />
              </div>
            </div>
            <div class="ccp__field-row ccp__field-row--3">
              <div class="ccp__field">
                <label class="ccp__label">
                  <span
                    class="ccp__lang-badge"
                    :class="
                      form1.bdLang === 'fr' ? 'ccp__lang-badge--active' : ''
                    "
                    >FR</span
                  >
                  {{ t('createCommerce.langFr') }}
                  <span
                    v-if="translatingBd && form1.bdLang !== 'fr'"
                    class="ccp__translating-dot"
                  />
                </label>
                <textarea
                  v-model="form1.bdFr"
                  class="ccp__textarea"
                  :class="form1.bdLang === 'fr' ? 'ccp__textarea--primary' : ''"
                  rows="2"
                  :placeholder="t('createCommerce.bannerFrPlaceholder')"
                  @blur="form1.bdLang === 'fr' && translateBd()"
                />
              </div>
              <div class="ccp__field">
                <label class="ccp__label">
                  <span
                    class="ccp__lang-badge"
                    :class="
                      form1.bdLang === 'en' ? 'ccp__lang-badge--active' : ''
                    "
                    >EN</span
                  >
                  {{ t('createCommerce.langEn') }}
                  <span
                    v-if="translatingBd && form1.bdLang !== 'en'"
                    class="ccp__translating-dot"
                  />
                </label>
                <textarea
                  v-model="form1.bdEn"
                  class="ccp__textarea"
                  :class="form1.bdLang === 'en' ? 'ccp__textarea--primary' : ''"
                  rows="2"
                  :placeholder="t('createCommerce.bannerEnPlaceholder')"
                  @blur="form1.bdLang === 'en' && translateBd()"
                />
              </div>
              <div class="ccp__field">
                <label class="ccp__label">
                  <span
                    class="ccp__lang-badge"
                    :class="
                      form1.bdLang === 'nl' ? 'ccp__lang-badge--active' : ''
                    "
                    >NL</span
                  >
                  {{ t('createCommerce.langNl') }}
                  <span
                    v-if="translatingBd && form1.bdLang !== 'nl'"
                    class="ccp__translating-dot"
                  />
                </label>
                <textarea
                  v-model="form1.bdNl"
                  class="ccp__textarea"
                  :class="form1.bdLang === 'nl' ? 'ccp__textarea--primary' : ''"
                  rows="2"
                  :placeholder="t('createCommerce.bannerNlPlaceholder')"
                  @blur="form1.bdLang === 'nl' && translateBd()"
                />
              </div>
            </div>
          </div>
        </section>

        <div v-if="error1" class="ccp__error-banner">
          <i class="pi pi-exclamation-circle" /> {{ error1 }}
        </div>

        <div class="ccp__actions">
          <button
            class="ccp__btn ccp__btn--ghost"
            @click="router.push('/commerces')"
          >
            <i class="pi pi-times" /> {{ t('createCommerce.cancel') }}
          </button>
          <button
            class="ccp__btn ccp__btn--primary"
            :disabled="isSaving1"
            @click="submitStep1"
          >
            <span v-if="isSaving1" class="ccp__spinner ccp__spinner--white" />
            <i v-else class="pi pi-arrow-right" />
            {{
              isSaving1
                ? t('createCommerce.saving1')
                : t('createCommerce.continue')
            }}
          </button>
        </div>
      </div>

      <!-- ══ Étape 2 : Horaires ═════════════════════════════════════════════════ -->
      <div
        v-else-if="step === 2"
        class="ccp__body"
        @click.self="closeTimePicker"
      >
        <section class="ccp__section">
          <div class="ccp__section-hd">
            <div class="ccp__section-ico"><i class="pi pi-clock" /></div>
            <div>
              <p class="ccp__section-title">
                {{ t('createCommerce.step2Title') }}
              </p>
              <p class="ccp__section-sub">{{ t('createCommerce.step2Sub') }}</p>
            </div>
          </div>

          <div class="ccp__week">
            <div
              v-for="day in WEEKDAYS"
              :key="day"
              class="ccp__week-row"
              :class="{ 'ccp__week-row--off': !weekSchedule[day].enabled }"
            >
              <!-- Toggle + nom du jour -->
              <div class="ccp__week-day-col">
                <label class="ccp__toggle" @click.stop>
                  <input
                    type="checkbox"
                    v-model="weekSchedule[day].enabled"
                    class="ccp__toggle-input"
                  />
                  <span class="ccp__toggle-track"
                    ><span class="ccp__toggle-thumb"
                  /></span>
                </label>
                <div class="ccp__week-day-info">
                  <span class="ccp__week-day-name">{{
                    WEEKDAY_LABELS[day]
                  }}</span>
                  <span
                    v-if="!weekSchedule[day].enabled"
                    class="ccp__week-closed-badge"
                    ><i class="pi pi-ban" />
                    {{ t('createCommerce.closed') }}</span
                  >
                </div>
              </div>

              <!-- Plages horaires -->
              <div class="ccp__week-time-col">
                <template v-if="weekSchedule[day].enabled">
                  <!-- Ouverture -->
                  <div class="ccp__tp-wrap">
                    <button
                      class="ccp__tp-trigger ccp__tp-trigger--open"
                      @click.stop="toggleTimePicker(`${day}-open`)"
                    >
                      <span class="ccp__tp-val">{{
                        weekSchedule[day].open
                      }}</span>
                      <i class="pi pi-chevron-down ccp__tp-caret" />
                    </button>
                    <div
                      v-if="timePickerOpen === `${day}-open`"
                      class="ccp__tp-drop"
                      @click.stop
                    >
                      <div class="ccp__tp-head">
                        {{ t('createCommerce.openTime') }}
                      </div>
                      <div class="ccp__tp-cols">
                        <div class="ccp__tp-col">
                          <button
                            class="ccp__tp-arr"
                            @click="adjustHour(day, 'open', 1)"
                          >
                            <i class="pi pi-chevron-up" />
                          </button>
                          <span class="ccp__tp-num">{{
                            parseTime(weekSchedule[day].open)
                              .h.toString()
                              .padStart(2, '0')
                          }}</span>
                          <button
                            class="ccp__tp-arr"
                            @click="adjustHour(day, 'open', -1)"
                          >
                            <i class="pi pi-chevron-down" />
                          </button>
                        </div>
                        <span class="ccp__tp-colon">:</span>
                        <div class="ccp__tp-col">
                          <button
                            class="ccp__tp-arr"
                            @click="adjustMin(day, 'open', 15)"
                          >
                            <i class="pi pi-chevron-up" />
                          </button>
                          <span class="ccp__tp-num">{{
                            parseTime(weekSchedule[day].open)
                              .m.toString()
                              .padStart(2, '0')
                          }}</span>
                          <button
                            class="ccp__tp-arr"
                            @click="adjustMin(day, 'open', -15)"
                          >
                            <i class="pi pi-chevron-down" />
                          </button>
                        </div>
                      </div>
                      <button class="ccp__tp-ok" @click="closeTimePicker">
                        <i class="pi pi-check" /> OK
                      </button>
                    </div>
                  </div>

                  <div class="ccp__week-arrow">
                    <i class="pi pi-arrow-right" />
                  </div>

                  <!-- Fermeture -->
                  <div class="ccp__tp-wrap">
                    <button
                      class="ccp__tp-trigger ccp__tp-trigger--close"
                      @click.stop="toggleTimePicker(`${day}-close`)"
                    >
                      <span class="ccp__tp-val">{{
                        weekSchedule[day].close
                      }}</span>
                      <i class="pi pi-chevron-down ccp__tp-caret" />
                    </button>
                    <div
                      v-if="timePickerOpen === `${day}-close`"
                      class="ccp__tp-drop ccp__tp-drop--right"
                      @click.stop
                    >
                      <div class="ccp__tp-head">
                        {{ t('createCommerce.closeTime') }}
                      </div>
                      <div class="ccp__tp-cols">
                        <div class="ccp__tp-col">
                          <button
                            class="ccp__tp-arr"
                            @click="adjustHour(day, 'close', 1)"
                          >
                            <i class="pi pi-chevron-up" />
                          </button>
                          <span class="ccp__tp-num">{{
                            parseTime(weekSchedule[day].close)
                              .h.toString()
                              .padStart(2, '0')
                          }}</span>
                          <button
                            class="ccp__tp-arr"
                            @click="adjustHour(day, 'close', -1)"
                          >
                            <i class="pi pi-chevron-down" />
                          </button>
                        </div>
                        <span class="ccp__tp-colon">:</span>
                        <div class="ccp__tp-col">
                          <button
                            class="ccp__tp-arr"
                            @click="adjustMin(day, 'close', 15)"
                          >
                            <i class="pi pi-chevron-up" />
                          </button>
                          <span class="ccp__tp-num">{{
                            parseTime(weekSchedule[day].close)
                              .m.toString()
                              .padStart(2, '0')
                          }}</span>
                          <button
                            class="ccp__tp-arr"
                            @click="adjustMin(day, 'close', -15)"
                          >
                            <i class="pi pi-chevron-down" />
                          </button>
                        </div>
                      </div>
                      <button class="ccp__tp-ok" @click="closeTimePicker">
                        <i class="pi pi-check" /> OK
                      </button>
                    </div>
                  </div>
                </template>
              </div>
            </div>
          </div>
        </section>

        <div v-if="error2" class="ccp__error-banner">
          <i class="pi pi-exclamation-circle" /> {{ error2 }}
        </div>

        <div class="ccp__actions">
          <button class="ccp__btn ccp__btn--ghost" @click="step = 1">
            <i class="pi pi-arrow-left" /> {{ t('createCommerce.back') }}
          </button>
          <button class="ccp__btn ccp__btn--ghost" @click="step = 3">
            {{ t('createCommerce.skip') }}
          </button>
          <button
            class="ccp__btn ccp__btn--primary"
            :disabled="isSaving2"
            @click="submitStep2"
          >
            <span v-if="isSaving2" class="ccp__spinner ccp__spinner--white" />
            <i v-else class="pi pi-arrow-right" />
            {{
              isSaving2
                ? t('createCommerce.saving2')
                : t('createCommerce.continue')
            }}
          </button>
        </div>
      </div>

      <!-- ══ Étape 3 : Contacts ═════════════════════════════════════════════════ -->
      <div v-else-if="step === 3" class="ccp__body">
        <section class="ccp__section">
          <div class="ccp__section-hd">
            <div class="ccp__section-ico"><i class="pi pi-users" /></div>
            <div>
              <p class="ccp__section-title">
                {{ t('createCommerce.step3Title') }}
              </p>
              <p class="ccp__section-sub">{{ t('createCommerce.step3Sub') }}</p>
            </div>
          </div>

          <div class="ccp__contacts">
            <div v-for="(c, i) in contacts" :key="i" class="ccp__contact-card">
              <div class="ccp__contact-card-hd">
                <span class="ccp__contact-num">{{
                  t('createCommerce.contactN', { n: i + 1 })
                }}</span>
                <label class="ccp__primary-toggle">
                  <input
                    type="radio"
                    :checked="c.isPrimary"
                    @change="setPrimary(i)"
                  />
                  {{ t('createCommerce.primaryContact') }}
                </label>
                <button
                  v-if="contacts.length > 1"
                  class="ccp__remove-btn"
                  @click="removeContact(i)"
                >
                  <i class="pi pi-trash" />
                </button>
              </div>
              <div class="ccp__fields">
                <div class="ccp__field-row">
                  <div class="ccp__field">
                    <label class="ccp__label">{{
                      t('createCommerce.firstName')
                    }}</label>
                    <input
                      v-model="c.firstName"
                      class="ccp__input"
                      type="text"
                      :placeholder="t('createCommerce.firstNamePlaceholder')"
                    />
                  </div>
                  <div class="ccp__field">
                    <label class="ccp__label">{{
                      t('createCommerce.lastName')
                    }}</label>
                    <input
                      v-model="c.lastName"
                      class="ccp__input"
                      type="text"
                      :placeholder="t('createCommerce.lastNamePlaceholder')"
                    />
                  </div>
                </div>
                <div class="ccp__field-row">
                  <div class="ccp__field">
                    <label class="ccp__label">{{
                      t('createCommerce.email')
                    }}</label>
                    <input
                      v-model="c.email"
                      class="ccp__input"
                      type="email"
                      :placeholder="t('createCommerce.emailPlaceholder')"
                    />
                  </div>
                  <div class="ccp__field">
                    <label class="ccp__label">{{
                      t('createCommerce.phone')
                    }}</label>
                    <input
                      v-model="c.phone"
                      class="ccp__input"
                      type="tel"
                      :placeholder="t('createCommerce.phonePlaceholder')"
                    />
                  </div>
                </div>
                <div class="ccp__field-row">
                  <div class="ccp__field">
                    <label class="ccp__label">{{
                      t('createCommerce.position')
                    }}</label>
                    <div class="ccp__select-wrap">
                      <select v-model="c.position" class="ccp__select">
                        <option
                          v-for="p in POSITIONS"
                          :key="p.value"
                          :value="p.value"
                        >
                          {{ p.label }}
                        </option>
                      </select>
                      <i class="pi pi-chevron-down ccp__select-icon" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <button class="ccp__add-btn" @click="addContact">
            <i class="pi pi-plus" /> {{ t('createCommerce.addContact') }}
          </button>
        </section>

        <div v-if="error3" class="ccp__error-banner">
          <i class="pi pi-exclamation-circle" /> {{ error3 }}
        </div>

        <div class="ccp__actions">
          <button class="ccp__btn ccp__btn--ghost" @click="step = 2">
            <i class="pi pi-arrow-left" /> {{ t('createCommerce.back') }}
          </button>
          <button class="ccp__btn ccp__btn--ghost" @click="done = true">
            {{ t('createCommerce.skip') }}
          </button>
          <button
            class="ccp__btn ccp__btn--primary"
            :disabled="isSaving3"
            @click="submitStep3"
          >
            <span v-if="isSaving3" class="ccp__spinner ccp__spinner--white" />
            <i v-else class="pi pi-check" />
            {{
              isSaving3
                ? t('createCommerce.saving3')
                : t('createCommerce.createBtn')
            }}
          </button>
        </div>
      </div>
    </template>
  </div>
</template>

<style scoped>
.ccp {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  min-width: 0;
  padding: 2.5rem 3rem;
  box-sizing: border-box;
}
@media (max-width: 1023px) {
  .ccp {
    padding: 1.5rem;
  }
}
@media (max-width: 639px) {
  .ccp {
    padding: 1rem;
    gap: 1rem;
  }
}

/* Breadcrumb */
.ccp__bc {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  font-size: 0.8125rem;
  color: var(--nxt-on-surface-muted);
}
.ccp__bc-link {
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  font-size: 0.8125rem;
  font-family: inherit;
  color: var(--nxt-on-surface-muted);
  transition: color 0.15s;
}
.ccp__bc-link:hover {
  color: var(--nxt-primary);
}
.ccp__bc-sep {
  font-size: 0.625rem;
}
.ccp__bc-cur {
  font-weight: 700;
  color: var(--nxt-on-surface);
}

/* Header */
.ccp__header {
  display: flex;
  align-items: center;
  gap: 1rem;
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.4);
  border-radius: 1.5rem;
  padding: 1.25rem 1.5rem;
  box-shadow: 0 4px 15px rgba(142, 142, 142, 0.06);
  transition:
    border-color 0.2s,
    box-shadow 0.2s;
}
.ccp__back {
  width: 2.25rem;
  height: 2.25rem;
  border-radius: 50%;
  flex-shrink: 0;
  border: 1px solid var(--nxt-outline-variant);
  background: var(--nxt-surface-low);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.875rem;
  color: var(--nxt-on-surface-variant);
  cursor: pointer;
  transition: all 0.15s;
}
.ccp__back:hover {
  background: var(--nxt-surface-mid);
  color: var(--nxt-on-surface);
}
.ccp__title {
  margin: 0 0 0.15rem;
  font-size: 1.125rem;
  font-weight: 800;
  letter-spacing: -0.02em;
  color: var(--nxt-on-surface);
}
.ccp__sub {
  margin: 0;
  font-size: 0.8125rem;
  color: var(--nxt-on-surface-muted);
}

/* Stepper */
.ccp__stepper {
  display: flex;
  align-items: center;
  background: var(--nxt-card-bg);
  border: 1px solid var(--nxt-outline-variant);
  border-radius: 1.25rem;
  padding: 1.25rem 1.5rem;
  box-shadow: var(--nxt-shadow-sm);
  gap: 0;
}
.ccp__step {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  flex: 1;
}
.ccp__step-dot {
  width: 2.25rem;
  height: 2.25rem;
  border-radius: 50%;
  border: 2px solid var(--nxt-outline-variant);
  background: var(--nxt-surface-low);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.875rem;
  color: var(--nxt-on-surface-muted);
  flex-shrink: 0;
  transition: all 0.2s;
}
.ccp__step--active .ccp__step-dot {
  border-color: var(--nxt-primary);
  background: rgba(0, 0, 0, 0.1);
  color: var(--nxt-primary);
}
.ccp__step--done .ccp__step-dot {
  border-color: var(--nxt-primary);
  background: var(--nxt-primary);
  color: #fff;
}
.ccp__step-label {
  font-size: 0.8125rem;
  font-weight: 600;
  color: var(--nxt-on-surface-muted);
  transition: color 0.2s;
  white-space: nowrap;
}
.ccp__step--active .ccp__step-label {
  color: var(--nxt-primary);
  font-weight: 700;
}
.ccp__step--done .ccp__step-label {
  color: var(--nxt-on-surface);
}
.ccp__step-line {
  flex: 1;
  height: 2px;
  background: var(--nxt-outline-variant);
  margin: 0 0.75rem;
  border-radius: 9999px;
  transition: background 0.2s;
}
.ccp__step--done .ccp__step-line {
  background: var(--nxt-primary);
}

/* Body */
.ccp__body {
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.4);
  border-radius: 1.5rem;
  box-shadow: 0 4px 15px rgba(142, 142, 142, 0.06);
  padding: 2rem;
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

/* Section */
.ccp__section {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}
.ccp__section-hd {
  display: flex;
  align-items: flex-start;
  gap: 0.875rem;
}
.ccp__section-ico {
  width: 2.25rem;
  height: 2.25rem;
  border-radius: 0.625rem;
  flex-shrink: 0;
  background: rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(0, 0, 0, 0.15);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.9375rem;
  color: var(--nxt-primary);
}
.ccp__section-title {
  margin: 0 0 0.1rem;
  font-size: 0.9375rem;
  font-weight: 700;
  color: var(--nxt-on-surface);
}
.ccp__section-sub {
  margin: 0;
  font-size: 0.8125rem;
  color: var(--nxt-on-surface-muted);
}
.ccp__divider {
  height: 1px;
  background: var(--nxt-outline-variant);
}

/* Fields */
.ccp__fields {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}
.ccp__field-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
}
.ccp__field-row--3 {
  grid-template-columns: 1fr 1fr 1fr;
}
.ccp__field--span2 {
  grid-column: span 2;
}
@media (max-width: 640px) {
  .ccp__field-row {
    grid-template-columns: 1fr;
  }
  .ccp__field-row--3 {
    grid-template-columns: 1fr;
  }
  .ccp__field--span2 {
    grid-column: span 1;
  }
}
.ccp__field {
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
}
.ccp__label {
  font-size: 0.8125rem;
  font-weight: 600;
  color: var(--nxt-on-surface);
  display: flex;
  align-items: center;
  gap: 0.375rem;
}
.ccp__req {
  color: var(--nxt-error);
}
.ccp__hint {
  font-size: 0.75rem;
  color: var(--nxt-on-surface-muted);
}

.ccp__input-wrap {
  position: relative;
  display: flex;
  align-items: center;
}
.ccp__input-wrap .ccp__input {
  padding-right: 2.5rem;
}
.ccp__input-reset {
  position: absolute;
  right: 0.625rem;
  background: none;
  border: none;
  cursor: pointer;
  color: var(--nxt-on-surface-muted);
  font-size: 0.875rem;
  display: flex;
  align-items: center;
  padding: 0;
  transition: color 0.15s;
}
.ccp__input-reset:hover {
  color: var(--nxt-primary);
}
.ccp__input {
  height: 2.625rem;
  padding: 0 0.875rem;
  border: 1px solid var(--nxt-outline-variant);
  border-radius: 0.625rem;
  background: var(--nxt-surface-low);
  color: var(--nxt-on-surface);
  font-size: 0.875rem;
  font-family: inherit;
  outline: none;
  transition:
    border-color 0.15s,
    background 0.15s;
  width: 100%;
  box-sizing: border-box;
}
.ccp__input::placeholder {
  color: var(--nxt-on-surface-muted);
}
.ccp__input:focus {
  border-color: var(--nxt-primary);
  background: var(--nxt-card-bg);
}
.ccp__input--time {
  width: auto;
}
.ccp__input--error {
  border-color: var(--nxt-error, #dc2626);
}
.ccp__input--error:focus {
  border-color: var(--nxt-error, #dc2626);
}
.ccp__org-trigger--error {
  border-color: var(--nxt-error, #dc2626) !important;
  box-shadow: 0 0 0 1px var(--nxt-error, #dc2626);
}
.ccp__org-trigger--error:hover {
  border-color: var(--nxt-error, #dc2626) !important;
}
.ccp__field-error {
  margin: 0;
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--nxt-error, #dc2626);
}

.ccp__textarea {
  padding: 0.625rem 0.875rem;
  border: 1px solid var(--nxt-outline-variant);
  border-radius: 0.625rem;
  background: var(--nxt-surface-low);
  color: var(--nxt-on-surface);
  font-size: 0.875rem;
  font-family: inherit;
  outline: none;
  resize: vertical;
  transition: border-color 0.15s;
  width: 100%;
  box-sizing: border-box;
}
.ccp__textarea:focus {
  border-color: var(--nxt-primary);
  background: var(--nxt-card-bg);
}

.ccp__select-wrap {
  position: relative;
}
.ccp__select {
  width: 100%;
  height: 2.625rem;
  padding: 0 2.5rem 0 0.875rem;
  border: 1px solid var(--nxt-outline-variant);
  border-radius: 0.625rem;
  background: var(--nxt-surface-low);
  color: var(--nxt-on-surface);
  font-size: 0.875rem;
  font-family: inherit;
  outline: none;
  appearance: none;
  -webkit-appearance: none;
  cursor: pointer;
  transition: border-color 0.15s;
}
.ccp__select:focus {
  border-color: var(--nxt-primary);
}
.ccp__select-icon {
  position: absolute;
  right: 0.875rem;
  top: 50%;
  transform: translateY(-50%);
  font-size: 0.75rem;
  color: var(--nxt-on-surface-muted);
  pointer-events: none;
}

/* Lang badge */
.ccp__lang-badge {
  font-size: 0.5625rem;
  font-weight: 800;
  background: rgba(0, 0, 0, 0.08);
  color: var(--nxt-on-surface-muted);
  border: 1px solid var(--nxt-outline-variant);
  border-radius: 0.25rem;
  padding: 0.1rem 0.35rem;
  letter-spacing: 0.05em;
  transition: all 0.15s;
}
.ccp__lang-badge--active {
  background: rgba(0, 0, 0, 0.15);
  color: var(--nxt-primary);
  border-color: rgba(0, 0, 0, 0.3);
}
.ccp__textarea--primary {
  border-color: rgba(0, 0, 0, 0.35);
  background: rgba(0, 0, 0, 0.02);
}
.ccp__translating-dot {
  width: 0.45rem;
  height: 0.45rem;
  border-radius: 50%;
  background: var(--nxt-primary);
  display: inline-block;
  animation: ccp-pulse 0.8s ease-in-out infinite;
}
@keyframes ccp-pulse {
  0%,
  100% {
    opacity: 0.3;
    transform: scale(0.8);
  }
  50% {
    opacity: 1;
    transform: scale(1.2);
  }
}

/* Sélecteur organisation */
.ccp__org-selected {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  height: 2.625rem;
  padding: 0 0.875rem;
  border: 1px solid var(--nxt-primary);
  border-radius: 0.625rem;
  background: rgba(0, 0, 0, 0.06);
}
.ccp__org-selected-name {
  flex: 1;
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--nxt-on-surface);
}
.ccp__org-clear {
  background: none;
  border: none;
  cursor: pointer;
  color: var(--nxt-on-surface-muted);
  font-size: 0.875rem;
  display: flex;
  align-items: center;
  padding: 0;
  transition: color 0.15s;
}
.ccp__org-clear:hover {
  color: var(--nxt-error);
}
.ccp__org-trigger {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  height: 2.625rem;
  padding: 0 0.875rem;
  border: 1px solid var(--nxt-outline-variant);
  border-radius: 0.625rem;
  background: var(--nxt-surface-low);
  color: var(--nxt-on-surface-muted);
  font-size: 0.875rem;
  font-family: inherit;
  cursor: pointer;
  width: 100%;
  text-align: left;
  transition: border-color 0.15s;
}
.ccp__org-trigger:hover {
  border-color: var(--nxt-primary);
  color: var(--nxt-on-surface);
}
.ccp__org-drop {
  position: relative;
  z-index: 10;
  margin-top: 0.375rem;
  background: var(--nxt-card-bg);
  border: 1px solid var(--nxt-outline-variant);
  border-radius: 0.875rem;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}
.ccp__org-search-wrap {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  border-bottom: 1px solid var(--nxt-outline-variant);
}
.ccp__org-search-ico {
  font-size: 0.875rem;
  color: var(--nxt-on-surface-muted);
  flex-shrink: 0;
}
.ccp__org-search {
  flex: 1;
  border: none;
  background: none;
  font-size: 0.875rem;
  font-family: inherit;
  color: var(--nxt-on-surface);
  outline: none;
}
.ccp__org-search::placeholder {
  color: var(--nxt-on-surface-muted);
}
.ccp__org-close {
  background: none;
  border: none;
  cursor: pointer;
  color: var(--nxt-on-surface-muted);
  font-size: 0.875rem;
  display: flex;
  align-items: center;
  padding: 0;
  transition: color 0.15s;
  flex-shrink: 0;
}
.ccp__org-close:hover {
  color: var(--nxt-on-surface);
}
.ccp__org-list {
  max-height: 14rem;
  overflow-y: auto;
}
.ccp__org-loading {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  padding: 1rem 1.25rem;
  font-size: 0.875rem;
  color: var(--nxt-on-surface-muted);
}
.ccp__org-empty {
  padding: 1rem 1.25rem;
  font-size: 0.875rem;
  color: var(--nxt-on-surface-muted);
  text-align: center;
}
.ccp__org-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  width: 100%;
  padding: 0.75rem 1rem;
  border: none;
  background: none;
  cursor: pointer;
  text-align: left;
  transition: background 0.12s;
  font-family: inherit;
}
.ccp__org-item:hover {
  background: var(--nxt-surface-low);
}
.ccp__org-item-av {
  width: 2rem;
  height: 2rem;
  border-radius: 0.5rem;
  background: rgba(0, 0, 0, 0.1);
  color: var(--nxt-primary);
  font-size: 0.75rem;
  font-weight: 800;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.ccp__org-item-body {
  display: flex;
  flex-direction: column;
  min-width: 0;
}
.ccp__org-item-name {
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--nxt-on-surface);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.ccp__org-item-sub {
  font-size: 0.6875rem;
  color: var(--nxt-on-surface-muted);
  text-transform: uppercase;
  letter-spacing: 0.05em;
}
.ccp__org-pag {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  padding: 0.625rem 1rem;
  border-top: 1px solid var(--nxt-outline-variant);
}
.ccp__org-pag-btn {
  width: 1.75rem;
  height: 1.75rem;
  border-radius: 0.4rem;
  border: 1px solid var(--nxt-outline-variant);
  background: none;
  cursor: pointer;
  color: var(--nxt-on-surface-muted);
  font-size: 0.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s;
}
.ccp__org-pag-btn:disabled {
  opacity: 0.35;
  cursor: not-allowed;
}
.ccp__org-pag-btn:not(:disabled):hover {
  border-color: var(--nxt-primary);
  color: var(--nxt-primary);
}
.ccp__org-pag-info {
  font-size: 0.75rem;
  font-weight: 700;
  color: var(--nxt-on-surface-muted);
}

/* Horaires — calendrier semaine */
/* ── Horaires ─────────────────────────────────────────────────────────────── */
.ccp__week {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}
.ccp__week-row {
  display: flex;
  align-items: center;
  gap: 1rem;
  background: var(--nxt-card-bg);
  border: 1px solid var(--nxt-outline-variant);
  border-radius: 0.875rem;
  padding: 0.875rem 1.25rem;
  transition:
    opacity 0.2s,
    border-color 0.2s,
    box-shadow 0.2s;
}
.ccp__week-row:hover {
  border-color: var(--nxt-primary);
  box-shadow: 0 0 0 3px rgba(0, 0, 0, 0.06);
}
.ccp__week-row--off {
  opacity: 0.5;
}
.ccp__week-row--off:hover {
  border-color: var(--nxt-outline-variant);
  box-shadow: none;
}
.ccp__week-day-col {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  width: 11rem;
  flex-shrink: 0;
}
.ccp__week-day-info {
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
}
.ccp__week-day-name {
  font-size: 0.9375rem;
  font-weight: 700;
  color: var(--nxt-on-surface);
}
.ccp__week-closed-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.6875rem;
  font-weight: 600;
  color: var(--nxt-on-surface-muted);
}
.ccp__week-closed-badge .pi {
  font-size: 0.6875rem;
  color: var(--nxt-error);
  opacity: 0.6;
}
/* Toggle switch */
.ccp__toggle {
  display: inline-flex;
  align-items: center;
  cursor: pointer;
  flex-shrink: 0;
}
.ccp__toggle-input {
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
}
.ccp__toggle-track {
  width: 2.5rem;
  height: 1.375rem;
  border-radius: 9999px;
  background: var(--nxt-surface-high);
  position: relative;
  transition: background 0.2s;
}
.ccp__toggle-input:checked + .ccp__toggle-track {
  background: var(--nxt-primary);
}
.ccp__toggle-thumb {
  position: absolute;
  top: 0.1875rem;
  left: 0.1875rem;
  width: 1rem;
  height: 1rem;
  border-radius: 50%;
  background: #fff;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.25);
  transition: transform 0.2s;
}
.ccp__toggle-input:checked + .ccp__toggle-track .ccp__toggle-thumb {
  transform: translateX(1.125rem);
}
/* Time column */
.ccp__week-time-col {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex: 1;
  justify-content: flex-end;
}
.ccp__week-arrow {
  display: flex;
  align-items: center;
  color: var(--nxt-on-surface-muted);
  font-size: 0.875rem;
  flex-shrink: 0;
}

/* Time picker custom */
.ccp__tp-wrap {
  position: relative;
}
.ccp__tp-trigger {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  height: 2.5rem;
  padding: 0 0.875rem;
  border: 1.5px solid var(--nxt-outline-variant);
  border-radius: 0.75rem;
  background: var(--nxt-card-bg);
  cursor: pointer;
  font-family: inherit;
  transition:
    border-color 0.15s,
    box-shadow 0.15s;
}
.ccp__tp-trigger:hover {
  border-color: var(--nxt-primary);
  box-shadow: 0 0 0 3px rgba(0, 0, 0, 0.08);
}
.ccp__tp-val {
  font-size: 1rem;
  font-weight: 800;
  color: var(--nxt-on-surface);
  letter-spacing: 0.03em;
  min-width: 3.25rem;
  text-align: center;
}
.ccp__tp-caret {
  font-size: 0.625rem;
  color: var(--nxt-on-surface-muted);
}
.ccp__tp-drop {
  position: absolute;
  top: calc(100% + 0.5rem);
  left: 0;
  z-index: 100;
  background: var(--nxt-card-bg);
  border: 1px solid var(--nxt-outline-variant);
  border-radius: 1rem;
  box-shadow:
    0 12px 32px rgba(0, 0, 0, 0.14),
    0 2px 8px rgba(0, 0, 0, 0.06);
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  min-width: 10rem;
}
.ccp__tp-drop--right {
  left: auto;
  right: 0;
}
.ccp__tp-head {
  font-size: 0.75rem;
  font-weight: 700;
  color: var(--nxt-on-surface-muted);
  display: flex;
  align-items: center;
  gap: 0.35rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  border-bottom: 1px solid var(--nxt-outline-variant);
  padding-bottom: 0.5rem;
}
.ccp__tp-cols {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.25rem;
}
.ccp__tp-col {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.25rem;
}
.ccp__tp-arr {
  width: 2.25rem;
  height: 2.25rem;
  border-radius: 0.625rem;
  border: 1px solid var(--nxt-outline-variant);
  background: var(--nxt-surface-low);
  cursor: pointer;
  color: var(--nxt-on-surface-muted);
  font-size: 0.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s;
}
.ccp__tp-arr:hover {
  background: rgba(0, 0, 0, 0.08);
  border-color: var(--nxt-primary);
  color: var(--nxt-primary);
}
.ccp__tp-num {
  font-size: 1.625rem;
  font-weight: 900;
  color: var(--nxt-on-surface);
  letter-spacing: -0.02em;
  min-width: 2.75rem;
  text-align: center;
}
.ccp__tp-colon {
  font-size: 1.625rem;
  font-weight: 900;
  color: var(--nxt-on-surface-muted);
  padding: 0 0.125rem;
}
.ccp__tp-ok {
  height: 2.25rem;
  border-radius: 0.625rem;
  border: none;
  background: var(--nxt-gradient-primary);
  color: #fff;
  font-size: 0.8125rem;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.35rem;
  transition: filter 0.15s;
  box-shadow: var(--nxt-gradient-primary-btn-shadow);
}
.ccp__tp-ok:hover {
  filter: brightness(1.07);
}

/* Contacts */
.ccp__contacts {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}
.ccp__contact-card {
  background: var(--nxt-surface-low);
  border: 1px solid var(--nxt-outline-variant);
  border-radius: 0.875rem;
  padding: 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}
.ccp__contact-card-hd {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}
.ccp__contact-num {
  font-size: 0.8125rem;
  font-weight: 700;
  color: var(--nxt-on-surface);
  flex: 1;
}
.ccp__primary-toggle {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--nxt-on-surface-muted);
  cursor: pointer;
}
.ccp__primary-toggle input {
  accent-color: var(--nxt-primary);
  cursor: pointer;
}

/* Boutons utilitaires */
.ccp__add-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  height: 2.25rem;
  padding: 0 1rem;
  border-radius: 0.625rem;
  border: 1.5px dashed var(--nxt-outline-variant);
  background: none;
  font-size: 0.8125rem;
  font-weight: 700;
  font-family: inherit;
  color: var(--nxt-on-surface-muted);
  cursor: pointer;
  transition: all 0.15s;
  align-self: flex-start;
}
.ccp__add-btn:hover {
  border-color: var(--nxt-primary);
  color: var(--nxt-primary);
  background: rgba(0, 0, 0, 0.04);
}
.ccp__remove-btn {
  width: 2rem;
  height: 2rem;
  border-radius: 0.5rem;
  border: 1px solid var(--nxt-outline-variant);
  background: none;
  cursor: pointer;
  color: var(--nxt-on-surface-muted);
  font-size: 0.875rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s;
  flex-shrink: 0;
}
.ccp__remove-btn:hover {
  background: var(--nxt-error-container);
  border-color: var(--nxt-error);
  color: var(--nxt-error);
}

/* Boutons principaux */
.ccp__btn {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  height: 2.5rem;
  padding: 0 1.25rem;
  border-radius: 0.625rem;
  font-size: 0.875rem;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
  white-space: nowrap;
  border: none;
  transition:
    filter 0.15s,
    opacity 0.15s;
}
.ccp__btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}
.ccp__btn--primary {
  background: var(--nxt-gradient-primary);
  color: var(--nxt-on-primary);
  box-shadow: var(--nxt-gradient-primary-btn-shadow);
}
.ccp__btn--primary:not(:disabled):hover {
  filter: brightness(1.07);
}
.ccp__btn--ghost {
  background: var(--nxt-surface-low);
  color: var(--nxt-on-surface-variant);
  border: 1px solid var(--nxt-outline-variant);
}
.ccp__btn--ghost:hover {
  background: var(--nxt-surface-mid);
  color: var(--nxt-on-surface);
}

.ccp__actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  padding-top: 1.25rem;
  border-top: 1px solid var(--nxt-outline-variant);
  flex-wrap: wrap;
}
.ccp__error-banner {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  padding: 0.875rem 1.125rem;
  border-radius: 0.75rem;
  background: var(--nxt-error-container);
  border: 1px solid var(--nxt-on-error-container);
  font-size: 0.875rem;
  color: var(--nxt-on-error-container);
}

.ccp__spinner {
  width: 1rem;
  height: 1rem;
  border-radius: 50%;
  display: inline-block;
  border: 2px solid var(--nxt-outline-variant);
  border-top-color: var(--nxt-primary);
  animation: ccp-spin 0.7s linear infinite;
}
.ccp__spinner--white {
  border-color: rgba(255, 255, 255, 0.25);
  border-top-color: var(--nxt-on-primary);
}
@keyframes ccp-spin {
  to {
    transform: rotate(360deg);
  }
}

/* Succès */
.ccp__success {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  padding: 4rem 2rem;
  text-align: center;
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.4);
  border-radius: 1.5rem;
  box-shadow: 0 4px 15px rgba(142, 142, 142, 0.06);
}
.ccp__success-icon {
  width: 4rem;
  height: 4rem;
  border-radius: 50%;
  background: var(--nxt-status-up-bg);
  border: 2px solid var(--nxt-status-up);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  color: var(--nxt-status-up);
}
.ccp__success-title {
  margin: 0;
  font-size: 1.375rem;
  font-weight: 800;
  color: var(--nxt-on-surface);
}
.ccp__success-sub {
  margin: 0;
  font-size: 0.9375rem;
  color: var(--nxt-on-surface-muted);
  max-width: 28rem;
}
.ccp__success-actions {
  display: flex;
  gap: 0.75rem;
  margin-top: 0.5rem;
  flex-wrap: wrap;
  justify-content: center;
}
</style>

<style>
[data-theme='dark'] .ccp {
  background: #0c1421;
}
[data-theme='dark'] .ccp__header,
[data-theme='dark'] .ccp__body,
[data-theme='dark'] .ccp__success,
[data-theme='dark'] .ccp__stepper {
  background: rgba(20, 28, 41, 0.8);
  border-color: rgba(255, 255, 255, 0.08);
}
[data-theme='dark'] .ccp__step-dot {
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(255, 255, 255, 0.1);
}
[data-theme='dark'] .ccp__step--active .ccp__step-dot {
  background: rgba(255, 214, 10, 0.12);
  border-color: #ffd60a;
  color: #ffd60a;
}
[data-theme='dark'] .ccp__step--done .ccp__step-dot {
  background: #ffd60a;
  border-color: #ffd60a;
  color: #0b1320;
}
[data-theme='dark'] .ccp__step--active .ccp__step-label {
  color: #ffd60a;
}
[data-theme='dark'] .ccp__divider {
  background: rgba(255, 255, 255, 0.08);
}
[data-theme='dark'] .ccp__section-ico {
  background: rgba(255, 255, 255, 0.06);
  border-color: rgba(255, 255, 255, 0.08);
}
[data-theme='dark'] .ccp__input,
[data-theme='dark'] .ccp__textarea,
[data-theme='dark'] .ccp__select,
[data-theme='dark'] .ccp__org-trigger,
[data-theme='dark'] .ccp__org-search,
[data-theme='dark'] .ccp__tp-trigger,
[data-theme='dark'] .ccp__week-row,
[data-theme='dark'] .ccp__contact-card {
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(255, 255, 255, 0.1);
  color: #fff;
}
[data-theme='dark'] .ccp__input:focus,
[data-theme='dark'] .ccp__textarea:focus,
[data-theme='dark'] .ccp__select:focus,
[data-theme='dark'] .ccp__week-row:hover,
[data-theme='dark'] .ccp__tp-trigger:hover {
  border-color: #ffd60a;
  background: rgba(255, 255, 255, 0.08);
}
[data-theme='dark'] .ccp__org-drop,
[data-theme='dark'] .ccp__tp-drop {
  background: #111c2e;
  border-color: rgba(255, 255, 255, 0.1);
}
[data-theme='dark'] .ccp__org-item:hover {
  background: rgba(255, 255, 255, 0.08);
}
[data-theme='dark'] .ccp__tp-arr {
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.6);
}
[data-theme='dark'] .ccp__btn--ghost {
  background: rgba(255, 255, 255, 0.06);
  color: rgba(255, 255, 255, 0.6);
  border-color: rgba(255, 255, 255, 0.1);
}
[data-theme='dark'] .ccp__btn--ghost:hover {
  background: rgba(255, 255, 255, 0.1);
  color: #fff;
}
[data-theme='dark'] .ccp__back {
  background: rgba(255, 255, 255, 0.06);
  border-color: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.6);
}
[data-theme='dark'] .ccp__back:hover {
  background: rgba(255, 255, 255, 0.1);
  color: #fff;
}
[data-theme='dark'] .ccp__lang-badge {
  background: rgba(255, 255, 255, 0.08);
  color: rgba(255, 255, 255, 0.5);
  border-color: rgba(255, 255, 255, 0.1);
}
[data-theme='dark'] .ccp__lang-badge--active {
  background: #ffd60a;
  color: #0b1320;
  border-color: #ffd60a;
}
[data-theme='dark'] .ccp__textarea--primary {
  border-color: rgba(255, 214, 10, 0.4);
  background: rgba(255, 214, 10, 0.06);
}
</style>
